import { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  useTheme,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { Email, Lock, Visibility, VisibilityOff } from '@mui/icons-material';
import { useAuth } from '../hooks';
import { useNavigate } from 'react-router-dom';
import { colors } from '../theme/theme';
import { countyBillingService } from '../services/county-billing.service';

export const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await login({ email, password });

      // Redirect based on user type (primary) or role (fallback)
      const userType = response.data?.user?.user_type;
      const userRoles = response.data?.user?.roles?.map((r) => r.name) || [];

      // Check user_type first (primary method)
      if (userType === 'superadmin' || userRoles.includes('Super Admin')) {
        navigate('/superadmin/dashboard');
      } else if (userType === 'county') {
        // County admins: gate the dashboard behind an active billing config.
        // The status read is exempt from the billing middleware so it always returns.
        try {
          const statusRes = await countyBillingService.myStatus();
          const s = statusRes.data?.status;
          const blocked = s === 'no_config' || s === 'expired' || s === 'suspended';
          navigate(blocked ? '/county/billing-required' : '/dashboard');
        } catch {
          // Status read failed — fall through to dashboard; the route guard will recover from a 402 if billing is actually bad.
          navigate('/dashboard');
        }
      } else {
        navigate('/dashboard');
      }
    } catch (err: any) {
      console.error('Login error:', err);
      console.error('Error response:', err.response);
      setError(err.response?.data?.message || err.message || 'Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: `linear-gradient(135deg, ${colors.grey[50]} 0%, ${colors.primary[50]} 100%)`,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Decorative background elements */}
      <Box
        sx={{
          position: 'absolute',
          top: -100,
          right: -100,
          width: 400,
          height: 400,
          borderRadius: '50%',
          background: colors.gradients.overlay,
          opacity: 0.5,
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: -150,
          left: -150,
          width: 500,
          height: 500,
          borderRadius: '50%',
          background: colors.gradients.overlayStrong,
          opacity: 0.3,
        }}
      />

      <Card
        sx={{
          maxWidth: 480,
          width: '100%',
          mx: 2,
          borderRadius: 4,
          boxShadow: colors.shadows.xl,
          position: 'relative',
          zIndex: 1,
        }}
      >
        <CardContent sx={{ p: 5 }}>
          {/* Logo */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: 3,
                background: colors.gradients.primary,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 900,
                fontSize: '2.5rem',
                color: 'white',
                boxShadow: colors.shadows.button,
                mb: 2,
              }}
            >
              K
            </Box>
            <Typography
              variant="h3"
              component="div"
              sx={{
                fontWeight: 800,
                mb: 0.5,
                letterSpacing: '-2px',
                fontSize: '2rem',
              }}
            >
              <Box component="span" sx={{ color: colors.brand.kDark }}>K-</Box>
              <Box component="span" sx={{ color: colors.brand.payBlue }}>Pay</Box>
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: colors.grey[600],
                fontSize: '0.8125rem',
                fontWeight: 600,
                letterSpacing: '1px',
                textTransform: 'uppercase',
              }}
            >
              Admin Portal
            </Typography>
          </Box>

          <Typography
            variant="h5"
            component="h1"
            align="center"
            sx={{
              fontWeight: 700,
              color: colors.grey[900],
              mb: 1,
            }}
          >
            Welcome Back
          </Typography>
          <Typography
            variant="body2"
            align="center"
            sx={{
              mb: 4,
              color: colors.grey[600],
              fontSize: '0.9375rem',
            }}
          >
            Sign in to access your dashboard
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
              {error.includes('Network Error') && (
                <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                  Please make sure the Laravel backend is running at http://localhost:8000
                </Typography>
              )}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="Email Address"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              margin="normal"
              required
              autoFocus
              disabled={loading}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Email sx={{ color: colors.grey[400] }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                },
              }}
            />
            <TextField
              fullWidth
              label="Password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              margin="normal"
              required
              disabled={loading}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock sx={{ color: colors.grey[400] }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      disabled={loading}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                },
              }}
            />
            <Box sx={{ textAlign: 'right', mt: 1 }}>
              <Typography
                component="a"
                href="/forgot-password"
                variant="body2"
                sx={{ color: colors.primary.main, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
              >
                Forgot password?
              </Typography>
            </Box>
            <Button
              fullWidth
              type="submit"
              variant="contained"
              size="large"
              disabled={loading}
              sx={{
                mt: 2,
                mb: 2,
                height: 56,
                borderRadius: 2,
                background: colors.gradients.primary,
                boxShadow: colors.shadows.button,
                fontSize: '1rem',
                fontWeight: 600,
                textTransform: 'none',
                '&:hover': {
                  background: colors.gradients.primaryHover,
                  boxShadow: colors.shadows.buttonHover,
                  transform: 'translateY(-1px)',
                },
                '&:active': {
                  transform: 'translateY(0)',
                },
                '&:disabled': {
                  background: 'linear-gradient(135deg, #cbd5e1 0%, #94a3b8 100%)',
                  boxShadow: 'none',
                },
              }}
            >
              {loading ? (
                <CircularProgress size={24} sx={{ color: 'white' }} />
              ) : (
                'Sign In'
              )}
            </Button>
          </form>

          {/* Footer */}
          <Typography
            variant="caption"
            align="center"
            sx={{
              display: 'block',
              mt: 3,
              color: colors.grey[500],
              fontSize: '0.75rem',
            }}
          >
            © {new Date().getFullYear()} K-Pay. All rights reserved.
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
};
